import { useForm, FormProvider, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import {
	AlertDialog,
	Badge,
	Card,
	DropdownMenu,
	IconButton,
	Tooltip,
	Flex,
	Box,
} from '@radix-ui/themes'
import {
	Link,
	useFetcher,
	useLocation,
	useOutletContext,
} from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxBookmark, RxBookmarkFilled, RxPencil1, RxGear } from 'react-icons/rx'
import { useHydrated } from 'remix-utils/use-hydrated'

import { AddressList } from '~/components/common/address-list.tsx'
import {
	FormDefaults,
	FormDefaultsHiddenField,
} from '~/components/common/form-defaults.tsx'
import { SubmissionButton } from '~/components/common/submission-button.tsx'
import { Icon } from '~/components/ui/icon.tsx'
import { type GetCustomer } from '~/models/ecommerce-providers/sylius/shop/fetch/customer/get-customer'
import { type AddressNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/address'
import {
	PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS,
	PROFILE_ADDRESSES_INPUTS,
	profileAddressesSetDefaultAddressSchema,
} from '~/routes/($lang).profile.addresses/addresses.component'
import { PROFILE_ADDRESSES_INDEX_I18NEXT } from '~/routes/($lang).profile.addresses._index/index.component'
import { RemoveAddressItem } from '~/routes/($lang).profile.addresses._index/remove-address.item.tsx'
import { useProfileAddressesSetDefaultNotify } from '~/routes/($lang).profile.addresses._index/use-profile-addresses-set-default-notify.tsx'
import { type Context, ROOT_INPUTS } from '~/routes/_layout/root.component'
import { useOptimisticAddress } from '~/utils/hooks/use-optimistic-address.ts'
import { useOptimisticDefaultAddress } from '~/utils/hooks/use-optimistic-default-address.ts'
import { useOptimisticRemoveAddress } from '~/utils/hooks/use-optimistic-remove-address.ts'

/* -------------------------------------------------------------------------------------------------
 * AddressItem
 * -----------------------------------------------------------------------------------------------*/

type ItemProps = {
	customer: GetCustomer
	iri: string
	firstName: string
	lastName: string
	countryCode: string
	street: string
	city: string
	postcode: string
	id: number
	phoneNumber: string
	company: string
}

const AddressItem: React.FC<ItemProps> = ({ customer, iri, id, ...rest }) => {
	const { locale, setApp } = useOutletContext<Context>()
	const location = useLocation()
	const [open, setOpen] = React.useState(false)
	const { t } = useTranslation(
		PROFILE_ADDRESSES_INDEX_I18NEXT.routeProfileAddressesIndex,
	)
	const isHydrated = useHydrated()

	const defaultAddressFetcher = useFetcher({
		key: PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS.generateFetcherKey!({
			id: String(id),
		}),
	})
  const defaultAddressFormValues = {
    [ROOT_INPUTS.subaction]:
    PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS.subaction,
    [ROOT_INPUTS.noJs]: String(!isHydrated),
  }
	const [defaultAddressForm, defaultAddressFields] = useForm({
		lastResult: defaultAddressFetcher.data || undefined,
		defaultValue: {
			...defaultAddressFormValues,
			[PROFILE_ADDRESSES_INPUTS.defaultAddress]: iri,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: profileAddressesSetDefaultAddressSchema,
			})
		},
		shouldValidate: 'onSubmit',
	})

	useProfileAddressesSetDefaultNotify({
		setApp,
		defaultAddressFetcher,
		t,
		firstName: rest.firstName,
		lastName: rest.lastName,
	})

	const optimisticDefaultAddress = useOptimisticDefaultAddress({
		customer,
		iri,
		id,
	})

	const optimisticRemoveAddress = useOptimisticRemoveAddress({ id })

	const optimisticAddress = useOptimisticAddress({ id, ...rest })

	return !optimisticRemoveAddress ? (
		<Card
			size="3"
			className={cn({
				'after:shadow-accent-5':
					(customer!.defaultAddress as AddressNormalized | null)?.['@id'] ===
					iri,
			})}
		>
			<Flex direction="column" height="100%">
				{optimisticDefaultAddress && (
					<Box position="absolute" top="0" right="0" m="2">
						<Tooltip content={t('label.defaultAddress')}>
							<Badge className="!bg-transparent">
								<RxBookmarkFilled size="1.2em" />
							</Badge>
						</Tooltip>
					</Box>
				)}
				<AddressList address={optimisticAddress} />

				<Flex justify="end" align="end" gapX="2" mt="4" flexGrow="1">
					<AlertDialog.Root open={open} onOpenChange={setOpen}>
						<DropdownMenu.Root>
							<DropdownMenu.Trigger>
								<IconButton variant="soft" radius="full">
									<RxGear />
								</IconButton>
							</DropdownMenu.Trigger>
							<DropdownMenu.Content>
								<DropdownMenu.Item>
									<Flex
										asChild
										align="center"
										justify="between"
										gapX="3"
										width="100%"
									>
										<Link
											to={`/${locale}/profile/addresses/${iri!.split('/').pop()}${location.search}`}
											prefetch="viewport"
										>
											{t('items.button.editAddress')}
											<Icon>
												<RxPencil1 />
											</Icon>
										</Link>
									</Flex>
								</DropdownMenu.Item>

								{!optimisticDefaultAddress && (
									<FormProvider context={defaultAddressForm.context}>
										<DropdownMenu.Item asChild>
											<defaultAddressFetcher.Form
												method="post"
												action={`/${locale}${PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS.action}${location.search}`}
												preventScrollReset
												{...getFormProps(defaultAddressForm)}
												className="gap-0"
											>
												<FormDefaults
													form={defaultAddressForm}
													fields={defaultAddressFields}
                          values={defaultAddressFormValues}
												/>
												<FormDefaultsHiddenField
													meta={
														defaultAddressFields[
															PROFILE_ADDRESSES_INPUTS.defaultAddress
														]
													}
												/>
												<Flex asChild align="center" justify="between" gapX="4">
													<button type="submit">
														<SubmissionButton fetcher={defaultAddressFetcher}>
															{t('items.button.setAsDefaultAddressSubmit')}
															<RxBookmark />
														</SubmissionButton>
													</button>
												</Flex>
											</defaultAddressFetcher.Form>
										</DropdownMenu.Item>
									</FormProvider>
								)}

								{!optimisticDefaultAddress && (
									<RemoveAddressItem setOpen={setOpen} id={id} {...rest} />
								)}
							</DropdownMenu.Content>
						</DropdownMenu.Root>
					</AlertDialog.Root>
				</Flex>
			</Flex>
		</Card>
	) : null
}
AddressItem.displayName = 'AddressItem'

export { AddressItem }
